import { useEffect } from 'react';
import Footer from '@/pages/home/components/Footer';

export default function ArticleGuide() {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: 'YOYBUY Spreadsheet: Полное руководство по использованию табличного агента для заказа товаров из Китая',
    description: 'В этом подробном руководстве мы расскажем всё, что нужно знать о YOYBUY Spreadsheet — современном инструменте для организации заказов из Китая через проверенных агентов.',
    datePublished: '2026-07-04',
    dateModified: '2026-07-04',
    author: {
      '@type': 'Organization',
      name: 'YOYBUY Spreadsheet',
      url: 'https://yoybuyspreadsheet.lol/',
    },
    publisher: {
      '@type': 'Organization',
      name: 'YOYBUY Spreadsheet',
      url: 'https://yoybuyspreadsheet.lol/',
      logo: {
        '@type': 'ImageObject',
        url: 'https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/67a09e300463e31c534fce7fbe7473f7.jpeg',
      },
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': 'https://yoybuyspreadsheet.lol/articles/yoybuy-spreadsheet-guide',
    },
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />
      <main className="bg-background-50 min-h-screen">
        {/* Navbar */}
        <nav className="bg-background-50 sticky top-0 z-40 border-b border-background-200/50">
          <div className="max-w-4xl mx-auto px-6 flex items-center justify-between h-16">
            <a href="/" className="flex items-center gap-2.5 no-underline">
              <div className="w-8 h-8 rounded-full bg-accent-500 flex items-center justify-center">
                <span className="text-background-50 font-bold text-sm font-heading">Y</span>
              </div>
              <span className="font-bold text-foreground-950 text-sm whitespace-nowrap font-sans">
                yoybuy spreadsheet
              </span>
            </a>
            <a
              href="/"
              className="text-foreground-500 hover:text-foreground-950 text-sm font-medium font-sans no-underline transition-colors whitespace-nowrap"
            >
              <i className="ri-arrow-left-line w-4 h-4 inline-flex items-center justify-center mr-1"></i>
              На главную
            </a>
          </div>
        </nav>

        {/* Article Content */}
        <article className="max-w-3xl mx-auto px-6 py-12 md:py-20">
          {/* Header */}
          <header className="mb-12">
            <div className="flex items-center gap-3 mb-6">
              <span className="px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-medium font-sans whitespace-nowrap">
                Руководство
              </span>
              <span className="text-foreground-400 text-sm font-sans">
                Обновлено: Июль 2026
              </span>
            </div>
            <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl text-foreground-950 font-bold leading-tight mb-6">
              YOYBUY Spreadsheet: Полное руководство по использованию табличного агента для заказа товаров из Китая
            </h1>
            <p className="text-foreground-500 text-lg leading-relaxed font-sans">
              В этом подробном руководстве мы расскажем всё, что нужно знать о YOYBUY Spreadsheet — современном инструменте для организации заказов из Китая через проверенных агентов. Вы узнаете, как работает система табличных агентов, какие преимущества она даёт покупателям и как максимально эффективно использовать YOYBUY Spreadsheet для экономии времени и денег.
            </p>
          </header>

          {/* Section 1 */}
          <section className="mb-12">
            <h2 className="font-heading text-2xl md:text-3xl text-foreground-950 font-semibold mb-5">
              Что такое YOYBUY Spreadsheet?
            </h2>
            <p className="text-foreground-600 leading-relaxed mb-4 font-sans">
              YOYBUY Spreadsheet — это специализированный табличный инструмент, разработанный для упрощения процесса заказа товаров из Китая. В отличие от традиционных платформ-посредников, где каждый заказ оформляется через веб-интерфейс, YOYBUY Spreadsheet использует формат электронных таблиц, позволяя пользователям централизованно управлять десятками и сотнями заказов одновременно.
            </p>
            <p className="text-foreground-600 leading-relaxed mb-4 font-sans">
              Система построена на принципе <strong>spreadsheet agent</strong> — агента, который работает непосредственно с вашей таблицей заказов. Вы заполняете строки таблицы ссылками на товары с таких площадок, как <strong>1688</strong>, <strong>Taobao</strong>, <strong>Weidian</strong>, а агент обрабатывает каждый заказ: выкупает товар, проверяет качество, организует международную доставку.
            </p>
            <p className="text-foreground-600 leading-relaxed font-sans">
              Главное преимущество YOYBUY Spreadsheet перед конкурентами — <strong>прозрачность ценообразования</strong> и <strong>возможность массовой обработки заказов</strong>. Это делает его идеальным выбором как для индивидуальных покупателей, так и для владельцев малого бизнеса, занимающихся дропшиппингом или розничной торговлей.
            </p>
          </section>

          {/* Section 2 */}
          <section className="mb-12">
            <h2 className="font-heading text-2xl md:text-3xl text-foreground-950 font-semibold mb-5">
              Как работает система табличных агентов?
            </h2>
            <div className="space-y-6">
              <div className="flex gap-4">
                <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-primary-600 font-bold text-sm font-sans">1</span>
                </div>
                <div>
                  <h3 className="font-sans text-foreground-950 font-semibold text-lg mb-2">
                    Создание таблицы заказов
                  </h3>
                  <p className="text-foreground-600 leading-relaxed font-sans">
                    Вы получаете доступ к шаблону YOYBUY Spreadsheet и заполняете его ссылками на нужные товары, указывая размеры, цвета, количества и особые пожелания. Каждая строка таблицы — это отдельный заказ, что позволяет отслеживать статус каждого товара в реальном времени.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-primary-600 font-bold text-sm font-sans">2</span>
                </div>
                <div>
                  <h3 className="font-sans text-foreground-950 font-semibold text-lg mb-2">
                    Обработка агентом
                  </h3>
                  <p className="text-foreground-600 leading-relaxed font-sans">
                    Ваш персональный табличный агент проверяет каждую позицию, связывается с продавцами на 1688 или Taobao, уточняет наличие и актуальные цены. Если товара нет в наличии, агент предлагает альтернативы или возвращает средства.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-primary-600 font-bold text-sm font-sans">3</span>
                </div>
                <div>
                  <h3 className="font-sans text-foreground-950 font-semibold text-lg mb-2">
                    Проверка качества и консолидация
                  </h3>
                  <p className="text-foreground-600 leading-relaxed font-sans">
                    Все товары проходят проверку качества на складе агента в Китае. Агент фотографирует каждый товар и отправляет вам фото для подтверждения. После вашего одобрения товары консолидируются в одну посылку для международной отправки.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-primary-600 font-bold text-sm font-sans">4</span>
                </div>
                <div>
                  <h3 className="font-sans text-foreground-950 font-semibold text-lg mb-2">
                    Международная доставка
                  </h3>
                  <p className="text-foreground-600 leading-relaxed font-sans">
                    Агент подбирает оптимальный способ доставки с учётом веса, габаритов и срочности. Доступны различные варианты: от экономичной морской доставки до экспресс-курьерских служб. Вы получаете трекинг-номер для отслеживания посылки.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Section 3 - Comparison Table */}
          <section className="mb-12">
            <h2 className="font-heading text-2xl md:text-3xl text-foreground-950 font-semibold mb-5">
              Сравнение платформ-агентов
            </h2>
            <div className="overflow-x-auto rounded-2xl border border-background-200">
              <table className="w-full text-left">
                <thead>
                  <tr className="bg-background-100">
                    <th className="px-5 py-4 text-foreground-700 font-semibold text-sm font-sans whitespace-nowrap">
                      Платформа
                    </th>
                    <th className="px-5 py-4 text-foreground-700 font-semibold text-sm font-sans whitespace-nowrap">
                      Цена
                    </th>
                    <th className="px-5 py-4 text-foreground-700 font-semibold text-sm font-sans whitespace-nowrap">
                      Скорость
                    </th>
                    <th className="px-5 py-4 text-foreground-700 font-semibold text-sm font-sans whitespace-nowrap">
                      Надёжность
                    </th>
                    <th className="px-5 py-4 text-foreground-700 font-semibold text-sm font-sans whitespace-nowrap">
                      Для кого
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-t border-background-200">
                    <td className="px-5 py-4">
                      <strong className="text-foreground-950 font-sans">YOYBUY Spreadsheet</strong>
                    </td>
                    <td className="px-5 py-4">
                      <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-accent-100 text-accent-700 text-xs font-medium font-sans whitespace-nowrap">
                        Низкая
                      </span>
                    </td>
                    <td className="px-5 py-4 text-foreground-600 text-sm font-sans">
                      Быстрая
                    </td>
                    <td className="px-5 py-4 text-foreground-600 text-sm font-sans">
                      Высокая
                    </td>
                    <td className="px-5 py-4 text-foreground-600 text-sm font-sans">
                      Новички
                    </td>
                  </tr>
                  <tr className="border-t border-background-200">
                    <td className="px-5 py-4 text-foreground-600 font-sans">
                      Superbuy
                    </td>
                    <td className="px-5 py-4">
                      <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-secondary-100 text-secondary-700 text-xs font-medium font-sans whitespace-nowrap">
                        Средняя
                      </span>
                    </td>
                    <td className="px-5 py-4 text-foreground-600 text-sm font-sans">
                      Быстрая
                    </td>
                    <td className="px-5 py-4 text-foreground-600 text-sm font-sans">
                      Высокая
                    </td>
                    <td className="px-5 py-4 text-foreground-600 text-sm font-sans">
                      Продвинутые
                    </td>
                  </tr>
                  <tr className="border-t border-background-200">
                    <td className="px-5 py-4 text-foreground-600 font-sans">
                      Kakobuy
                    </td>
                    <td className="px-5 py-4">
                      <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-accent-100 text-accent-700 text-xs font-medium font-sans whitespace-nowrap">
                        Низкая
                      </span>
                    </td>
                    <td className="px-5 py-4 text-foreground-600 text-sm font-sans">
                      Средняя
                    </td>
                    <td className="px-5 py-4 text-foreground-600 text-sm font-sans">
                      Средняя
                    </td>
                    <td className="px-5 py-4 text-foreground-600 text-sm font-sans">
                      Бюджет
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          {/* Section 4 */}
          <section className="mb-12">
            <h2 className="font-heading text-2xl md:text-3xl text-foreground-950 font-semibold mb-5">
              Преимущества YOYBUY Spreadsheet
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                {
                  title: 'Прозрачность цен',
                  desc: 'Вы всегда видите точную стоимость каждого товара у поставщика, комиссию агента и стоимость доставки. Никаких скрытых платежей и неожиданных наценок.',
                  icon: 'ri-eye-line',
                },
                {
                  title: 'Массовая обработка',
                  desc: 'Одна таблица может содержать сотни заказов. Это идеально для оптовых закупок, дропшиппинга и регулярных поставок товаров.',
                  icon: 'ri-stack-line',
                },
                {
                  title: 'Контроль качества',
                  desc: 'Каждый товар фотографируется на складе агента перед отправкой. Вы подтверждаете качество каждого заказа индивидуально.',
                  icon: 'ri-shield-check-line',
                },
                {
                  title: 'Гибкая доставка',
                  desc: 'Широкий выбор способов доставки: от экономичных до экспресс-вариантов. Консолидация заказов для снижения стоимости пересылки.',
                  icon: 'ri-ship-line',
                },
              ].map((item) => (
                <div
                  key={item.title}
                  className="bg-background-100 rounded-2xl p-6 border border-background-200/60"
                >
                  <div className="w-10 h-10 rounded-xl bg-accent-100 flex items-center justify-center mb-4">
                    <i className={`${item.icon} text-accent-600 text-lg`}></i>
                  </div>
                  <h3 className="font-sans text-foreground-950 font-semibold mb-2">
                    {item.title}
                  </h3>
                  <p className="text-foreground-500 text-sm leading-relaxed font-sans">
                    {item.desc}
                  </p>
                </div>
              ))}
            </div>
          </section>

          {/* Section 5 - FAQ */}
          <section className="mb-12">
            <h2 className="font-heading text-2xl md:text-3xl text-foreground-950 font-semibold mb-5">
              Часто задаваемые вопросы
            </h2>
            <div className="space-y-4">
              {[
                {
                  q: 'Безопасно ли использовать YOYBUY Spreadsheet?',
                  a: 'Да, YOYBUY Spreadsheet — это полностью безопасный инструмент. Все транзакции проходят через проверенную платформу Kaspreadsheet, которая работает с лицензированными агентами в Китае. Ваши платёжные данные защищены, а каждый агент проходит строгую верификацию перед началом работы.',
                },
                {
                  q: 'Сколько стоит использование YOYBUY Spreadsheet?',
                  a: 'Стоимость зависит от объёма заказов и выбранного тарифного плана. Комиссия агента обычно составляет от 5% до 10% от стоимости товара, что значительно ниже, чем у большинства конкурентов. Точные расценки доступны на сайте Kaspreadsheet после регистрации таблицы.',
                },
                {
                  q: 'Какие площадки поддерживает YOYBUY Spreadsheet?',
                  a: 'YOYBUY Spreadsheet поддерживает заказы с основных китайских торговых площадок: 1688 (Alibaba), Taobao, Tmall, Weidian, JD.com и других. Если товар продаётся на китайском маркетплейсе, агент сможет его выкупить.',
                },
                {
                  q: 'Как долго идёт доставка из Китая?',
                  a: 'Сроки доставки зависят от выбранного способа: экспресс-доставка (DHL, FedEx) — 5–10 дней, авиапочта — 10–20 дней, морская доставка — 25–45 дней. Вы можете выбрать оптимальный вариант с учётом срочности и бюджета.',
                },
                {
                  q: 'Можно ли использовать YOYBUY Spreadsheet для дропшиппинга?',
                  a: 'Да, YOYBUY Spreadsheet отлично подходит для дропшиппинга. Вы можете организовать массовые заказы через таблицу, а агент возьмёт на себя всю логистику. Многие продавцы на Etsy, eBay и Shopify уже используют табличных агентов для автоматизации поставок из Китая.',
                },
              ].map((faq) => (
                <details key={faq.q} className="group bg-background-100 rounded-2xl border border-background-200/60">
                  <summary className="px-6 py-5 cursor-pointer font-sans text-foreground-950 font-semibold flex items-center justify-between list-none">
                    {faq.q}
                    <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform w-5 h-5 flex items-center justify-center"></i>
                  </summary>
                  <div className="px-6 pb-5 text-foreground-500 leading-relaxed text-sm font-sans">
                    {faq.a}
                  </div>
                </details>
              ))}
            </div>
          </section>

          {/* CTA */}
          <section className="bg-foreground-950 rounded-3xl p-8 md:p-12 text-center mb-12">
            <h2 className="font-heading text-2xl md:text-3xl text-background-50 font-semibold mb-4">
              Начните использовать YOYBUY Spreadsheet
            </h2>
            <p className="text-foreground-300 mb-8 max-w-lg mx-auto font-sans">
              Присоединяйтесь к тысячам покупателей, которые уже экономят с YOYBUY Spreadsheet. Создайте свою первую таблицу заказов прямо сейчас!
            </p>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center gap-3 px-10 py-4 bg-accent-500 text-background-50 rounded-full font-semibold text-base hover:bg-accent-600 transition-colors no-underline whitespace-nowrap cursor-pointer"
            >
              Перейти к YOYBUY Spreadsheet
              <i className="ri-arrow-right-up-line w-5 h-5 flex items-center justify-center"></i>
            </a>
          </section>

          {/* Internal Links */}
          <nav className="border-t border-background-200 pt-8 mb-12">
            <h3 className="font-sans text-foreground-950 font-semibold text-lg mb-4">
              Полезные ссылки
            </h3>
            <div className="flex flex-wrap gap-3">
              <a
                href="/"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-background-100 text-foreground-700 hover:text-foreground-950 text-sm font-medium font-sans no-underline transition-colors whitespace-nowrap"
              >
                <i className="ri-home-line w-4 h-4 flex items-center justify-center"></i>
                Главная YOYBUY Spreadsheet
              </a>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-background-100 text-foreground-700 hover:text-foreground-950 text-sm font-medium font-sans no-underline transition-colors whitespace-nowrap"
              >
                <i className="ri-external-link-line w-4 h-4 flex items-center justify-center"></i>
                Kaspreadsheet
              </a>
              <a
                href="/shopping-guide"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-background-100 text-foreground-700 hover:text-foreground-950 text-sm font-medium font-sans no-underline transition-colors whitespace-nowrap"
              >
                <i className="ri-shopping-bag-line w-4 h-4 flex items-center justify-center"></i>
                Руководство по покупкам
              </a>
            </div>
          </nav>
        </article>

        <Footer />
      </main>
    </>
  );
}